import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';

import { useCan } from '@/components/can/Can';
import { DataTablePagination } from '@/components/dataTable/Pagination';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { Button } from '@/design-system/button';
import { Input } from '@/design-system/input';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { SearchIcon, SettingIcon } from '@/icons';
import { PlusIcon } from '@/icons/plus-icon';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { MainPage } from '@/services/mainPage/mainPage.types';
import { MainPageGroupForm } from '../form/MainPageGroupForm';
import { SetShuffleFormContainer } from '../form/SetShuffleFormContainer';
import useDeleteMainPageGroup from '../hooks/useDeleteMainPageGroup';
import { getColumns } from './Column';

export function MainPageTable({
  data,
  pagination,
  loading,
  locationId,
}: {
  data: MainPage[];
  pagination: Pagination;
  loading: boolean;
  locationId: string;
}) {
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { t } = useDynamicLocale();
  const onAddNew = () => {
    onOpen({
      title: 'Add New Group',
      okText: t('common.page.modal.submit'),
      size: 'lg',
      modalTitle: 'common.page.modal.manageGroup.title',
      component: <MainPageGroupForm locationId={locationId} />,
    });
  };

  const onSetting = () => {
    onOpen({
      title: 'Setting',
      okText: canUpdateShuffleTime ? t('common.page.modal.submit') : '',
      size: 'lg',
      modalTitle: 'common.page.modal.setShufflingTime.title',
      component: <SetShuffleFormContainer canUpdateShuffleTime={canUpdateShuffleTime} />,
    });
  };
  const { mutateAsync } = useDeleteMainPageGroup();
  const canGetStoreList = useCan({ allOf: [PERMS.recommendation.mainPage.store.get] });

  const columns = React.useMemo(
    () => getColumns(t, onOpen, openConfirmation, mutateAsync, locationId, canGetStoreList),
    [t, onOpen, openConfirmation, mutateAsync, locationId, canGetStoreList],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const canAddGroup = useCan({ allOf: [PERMS.recommendation.mainPage.group.create] });
  const canViewShuffleTime = useCan({ allOf: [PERMS.recommendation.mainPage.shuffle.get] });
  const canUpdateShuffleTime = useCan({ allOf: [PERMS.recommendation.mainPage.shuffle.update] });

  const { table } = useDataTable({
    data: data,
    columns,
    pageCount: pageCount,
    meta: {
      title: 'Main Page',
      totalPage: pagination.totalItems,
      loading: loading,
    },
    getRowId: (originalRow) => String(originalRow.groupId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table} className="font-medium">
        <div>
          <div className="flex justify-between px-4 py-3 border-b border-b-surface-hover">
            <div className="flex items-center rounded px-2 border border-surface-hover">
              <SearchIcon className="text-secondary stroke-1.5" size={20} />
              <Input
                placeholder={t('common.page.search')}
                value={(table.getState().globalFilter as string) ?? ''}
                onChange={(e) => {
                  const val = e.target.value;

                  const cleaned =
                    val
                      .match(
                        /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\u3400-\u4DBF\u0041-\u005A\u0061-\u007A\u0030-\u0039 @\u30FC\uFF0D\u2010\u2013\u2014\u2015\u301C]/g,
                      )
                      ?.join('') || '';

                  table.setGlobalFilter(cleaned);
                }}
                className="h-8 w-40 lg:w-56 border-0 outline-none focus-visible:ring-0 shadow-none"
              />
            </div>
            <div className="flex items-center gap-3">
              {canViewShuffleTime && (
                <Button variant={'outline'} className="bg-card" onClick={onSetting}>
                  <SettingIcon /> <Translation id="recommendationManagement.mainPage.button.setting" />
                </Button>
              )}
              {canAddGroup && (
                <Button onClick={onAddNew}>
                  <PlusIcon /> <Translation id="common.page.button.addNew" />
                </Button>
              )}
            </div>
          </div>
          <div className="border-b border-b-muted">
            <DataTablePagination table={table} />
          </div>
        </div>
      </DataTable>
    </>
  );
}
